import { useState, useMemo, useCallback } from 'react';
import { useApiCall } from '../utils/useApiCall';
import JobsService, { JobData, PaginatedJobsResponse } from '../api/JobsService';
import { JobDetails } from '../types/job';
import { transformJobToDetails } from '../utils/jobTransformations';
import { jobsData } from '../data/jobs';
import { useJobSearchApi } from './useJobSearchApi';

export const useJobManagement = () => {
  // Search and filter state
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  
  // Job details state
  const [isJobDetailsOpen, setIsJobDetailsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobDetails | null>(null);

  // Accumulated jobs discovered via search or pagination from any tab
  const [mergedJobs, setMergedJobs] = useState<JobData[]>([]);
  
  // Fetch jobs with pagination metadata in a single call
  const { 
    data: paginatedResponse,
    isLoading: isLoadingJobs,
    error: jobsError,
    execute: fetchJobsRaw
  } = useApiCall<PaginatedJobsResponse>(JobsService.getJobsPaginated);

  const rawApiJobs = useMemo(() => paginatedResponse?.jobs || null, [paginatedResponse]);

  const paginationMeta = useMemo(() => {
    if (!paginatedResponse) return null;
    return { total: paginatedResponse.total, totalPages: paginatedResponse.totalPages };
  }, [paginatedResponse]);

  const fetchJobs = useCallback(async (...args: unknown[]) => {
    const result = await fetchJobsRaw(undefined, 0, 50, true, ...args);
    return result?.jobs || [];
  }, [fetchJobsRaw]);

  const mergeJobs = useCallback((newJobs: JobData[]) => {
    setMergedJobs(prev => {
      const existingIds = new Set(prev.map(j => j.id));
      const truly = newJobs.filter(j => !existingIds.has(j.id));
      if (truly.length === 0) return prev;
      return [...prev, ...truly];
    });
  }, []);

  // Single source of truth: raw API jobs + all discovered jobs, deduped by ID
  const apiJobs = useMemo(() => {
    const base = rawApiJobs || [];
    if (mergedJobs.length === 0) return base;

    const seenIds = new Set(base.map(j => j.id));
    const extras = mergedJobs.filter(j => !seenIds.has(j.id));
    if (extras.length === 0) return base;
    return [...base, ...extras];
  }, [rawApiJobs, mergedJobs]);

  // Internal search API hook — triggers when Questionnaires tab searches
  const searchApi = useJobSearchApi(mergeJobs);
  
  // Filter jobs based on search keyword, division, and user
  const filteredJobs = useMemo(() => {
    if (!apiJobs || apiJobs.length === 0) return [];
    
    return apiJobs.filter(job => {
      const matchesKeyword = !searchKeyword.trim() || 
                        job.title.toLowerCase().includes(searchKeyword.toLowerCase());
      
      const matchesDivision = selectedDivision === 'all' || 
                          job.divisionId.toString() === selectedDivision;
      
      const matchesUser = selectedUser === 'all' || 
                      job.recruiterId.toString() === selectedUser;
      
      return matchesKeyword && matchesDivision && matchesUser;
    });
  }, [apiJobs, searchKeyword, selectedDivision, selectedUser]);

  // Handle search: client-side filter + API keyword search
  const handleSearch = useCallback((keyword: string, divisionId: string, userId: string) => {
    setSearchKeyword(keyword);
    setSelectedDivision(divisionId);
    setSelectedUser(userId);

    if (keyword?.trim()) {
      searchApi.searchByKeyword(keyword);
    } else {
      searchApi.clearSearch();
    }
  }, [searchApi]);
  
  const handleJobClick = (jobId: number) => {
    const job = apiJobs?.find(job => job.id === jobId);
    
    if (job) {
      const jobDetails = transformJobToDetails(job);
      setSelectedJob(jobDetails);
      setIsJobDetailsOpen(true);
    }
  };

  // Handle static job click for demo purposes
  const handleStaticJobClick = (jobKey: 'marketingDirector' | 'devOpsEngineer') => {
    // Create a modified version of the static job data that matches the updated interface
    const staticJobDetails: JobDetails = {
      title: jobsData[jobKey].title,
      location: jobsData[jobKey].location,
      function: jobsData[jobKey].function,
      industry: jobsData[jobKey].industry,
      hostedApplyUrl: jobsData[jobKey].hostedApplyUrl,
      contactEmail: jobsData[jobKey].contactEmail,
      description: `
        <p>${jobsData[jobKey].description}</p>
        <h3>About the Role</h3>
        <p>${jobsData[jobKey].aboutRole}</p>
        <h3>Responsibilities</h3>
        <ul>
          ${jobsData[jobKey].responsibilities.map(item => `<li>${item}</li>`).join('')}
        </ul>
      `,
      requiredSkills: `<ul>${jobsData[jobKey].requiredSkills.map(skill => `<li>${skill}</li>`).join('')}</ul>`
    };
    
    setSelectedJob(staticJobDetails);
    setIsJobDetailsOpen(true);
  };

  return {
    apiJobs,
    isLoadingJobs,
    jobsError,
    fetchJobs,
    mergeJobs,
    paginationMeta,
    filteredJobs,
    searchKeyword,
    selectedDivision,
    selectedUser,
    handleSearch,
    isJobDetailsOpen,
    setIsJobDetailsOpen,
    selectedJob,
    handleJobClick,
    handleStaticJobClick,
    closeJobDetails: () => setIsJobDetailsOpen(false)
  };
}; 